// Pure round-ticket (golf/minigolf) classification helpers, extracted from Tickets.jsx
// so the used/playing logic is unit-testable without rendering the component.

// Single shared testing flag (also used by Tickets.jsx for day-pass capping) - flip
// this to bypass all closing-time-based restrictions while developing after hours.
// Remember to set this back to false before deploying to production.
export const TESTING_MODE = false;

export function isSlotUsedValue(value) {
  return (
    value !== null &&
    value !== undefined &&
    value !== false &&
    value !== 0 &&
    value !== 'unused' &&
    value !== 'available'
  );
}

export function isGolfLikeTicket(ticket) {
  const name = (ticket?.name || '').toLowerCase();
  const category = (ticket?.category || '').toLowerCase();
  const ticketType = (ticket?.ticketType || '').toLowerCase();
  return category === 'golf' || name.includes('golf') || name.includes('minigolf') || ticketType === 'round' || ticketType === 'group-round';
}

// Returns true for round-based tickets (golf, minigolf) — no countdown needed
export function isRoundBased(ticket) {
  const name = (ticket?.name || '').toLowerCase();
  return ticket?.ticketType === 'round' || ticket?.ticketType === 'group-round' ||
    name.includes('golf') || name.includes('minigolf');
}

// Returns true for group golf tickets (multiple rounds/slots)
export function isGroupRound(ticket) {
  const name = (ticket?.name || '').toLowerCase();
  return ticket?.ticketType === 'group-round' || ticket?.groupSize > 1 || name.includes('gruppe');
}

// Number of slots for a group ticket
export function getGroupSize(ticket) {
  if (ticket?.groupSize) return Number(ticket.groupSize);
  return 4; // default group size
}

function normalizeActivatedAtMs(value) {
  if (!value) return null;
  if (typeof value === 'number') return value;
  if (typeof value === 'object') {
    if ('seconds' in value) return value.seconds * 1000;
    if ('_seconds' in value) return value._seconds * 1000;
  }
  return null;
}

function getClosingHourForDate(date) {
  const day = date.getDay(); // 0 = Sunday, 6 = Saturday
  return (day === 0 || day === 6) ? 19 : 21;
}

// A round ticket left "playing" past the closing hour of the day it was activated is
// stale (the park closed before anyone finished/marked it) - once that closing time has
// passed, treat it as used instead of leaving it in the Active section indefinitely.
export function isRoundExpiredByClosingTime(ticket) {
  if (TESTING_MODE) return false;
  if (!ticket?.isPlaying) return false;
  const activatedMs = normalizeActivatedAtMs(ticket?.activatedAt);
  if (!activatedMs) return false;
  const activatedDate = new Date(activatedMs);
  const closingTime = new Date(activatedDate);
  closingTime.setHours(getClosingHourForDate(activatedDate), 0, 0, 0);
  // If activation itself happened after that day's closing hour already passed (e.g. a
  // late-night activation), the same-day closing time is meaningless as a cutoff - use
  // the next day's closing time instead, so the ticket isn't stale the instant it starts.
  if (activatedMs >= closingTime.getTime()) {
    closingTime.setDate(closingTime.getDate() + 1);
    closingTime.setHours(getClosingHourForDate(closingTime), 0, 0, 0);
  }
  return Date.now() > closingTime.getTime();
}

// A round ticket is "used" once it has actually been played/finished — NOT just
// because it was printed at the kiosk. Kiosk printing only sets printedAt/isPrinted
// and must not be treated as "used", or the ticket would look finished before anyone played it.
export function isRoundTicketUsed(ticket) {
  return (
    ticket?.isActive === false ||
    Boolean(ticket?.usedAt) ||
    ticket?.isUsed === true ||
    String(ticket?.status || '').toLowerCase() === 'used' ||
    isRoundExpiredByClosingTime(ticket)
  );
}

export function isGroupRoundFullyUsed(ticket) {
  if (isRoundTicketUsed(ticket)) return true;
  if (!isGroupRound(ticket)) return false;

  const size = getGroupSize(ticket);

  const usedCount = Number(ticket?.usedCount ?? ticket?.usedSlots ?? ticket?.roundsUsed);
  if (Number.isFinite(usedCount) && usedCount >= size) return true;

  const roundsLeft = Number(ticket?.roundsLeft ?? ticket?.remainingRounds ?? ticket?.remainingSlots);
  if (Number.isFinite(roundsLeft) && roundsLeft <= 0) return true;

  if (Array.isArray(ticket?.slots) && ticket.slots.length > 0) {
    const usedSlots = ticket.slots.filter((v) => isSlotUsedValue(v)).length;
    if (usedSlots >= size) return true;
  }

  return false;
}

export function isRoundFullyUsed(ticket) {
  return isGroupRound(ticket) ? isGroupRoundFullyUsed(ticket) : isRoundTicketUsed(ticket);
}

// A round ticket is "playing" once activated and not yet finished/used.
export function isRoundPlaying(ticket) {
  return !isRoundFullyUsed(ticket) && Boolean(ticket?.isPlaying);
}
